import React, { useEffect, useMemo, useRef, useState, Fragment } from "react";
import {
  ArrowDownRight,
  Brain,
  ChevronDown,
  Coins,
  Gift,
  Mail,
  MessageSquareText,
  MousePointerClick,
  Newspaper,
  Radio,
  Send,
  ShieldCheck,
  Sparkles,
  Target,
  UserMinus,
  Users,
} from "lucide-react";
import BoundaryDiagram from "./components/BoundaryDiagram";
import FractalShader from "./components/FractalShader";
import HorizontalGallery from "./components/HorizontalGallery";
import IntroOverlay from "./components/IntroOverlay";
import ParticleBurstText from "./components/ParticleBurstText";
import Reveal from "./components/Reveal";
import * as apiUtil from "../../util/apiUtil";
import "./brandpick-preflight.css";
import "./brandpick.css";

const FORM_TYPE = "BRAND_PICK_CONSULT";
const CONSULT_FIELD_MAX_LENGTHS = {
  companyName: 100,
  contactName: 50,
  email: 255,
} as const;

const getVisitorId = (): string => {
  const key = "BIZCHAT_FORM_VISITOR_ID";
  let visitorId = window.localStorage.getItem(key);
  if (!visitorId) {
    visitorId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(key, visitorId);
  }
  return visitorId;
};

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

const smoothstep = (a: number, b: number, x: number) => {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const renderWithEmphasis = (text: string, emphasis?: string) => {
  const renderText = (t: string) =>
    t.split('\n').map((line, i, arr) => (
      <Fragment key={i}>
        {line}
        {i < arr.length - 1 && <br />}
      </Fragment>
    ));

  if (!emphasis) return renderText(text);
  const index = text.indexOf(emphasis);
  if (index === -1) return renderText(text);

  return (
    <>
      {renderText(text.slice(0, index))}
      <strong className="tw-font-semibold tw-text-[#202020]">{renderText(emphasis)}</strong>
      {renderText(text.slice(index + emphasis.length))}
    </>
  );
};

const CONSULT_MESSAGE_MAX_LENGTH = 1000;

const formatPhoneNumber = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length < 4) return digits;
  if (digits.length < 8) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length < 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
};

const brandpickIntro =
  "카카오 소유의 '카카오톡 브랜드픽' 채널을 활용해 브랜드에 관심 있는 잠재 고객에게\n맞춤형 광고 메시지를 발송하는 상품으로, 기존 채널 메시지로 도달하기 어려웠던\n신규 고객까지 확장하여 접점을 확보할 수 있습니다.";

const brandpickFeatures = [
  {
    number: "01",
    icon: Radio,
    title: "카카오의 채널로 발송",
    tagline: "광고주 채널이 아닌, 카카오가 운영하는 채널로 발송",
    detail:
      "카카오톡 브랜드픽 메시지는 광고주가 보유한 채널이 아닌,\n카카오가 직접 운영하는 ‘카카오톡 브랜드픽’ 채널을 통해 발송됩니다.\n기존 고객이나 보유 채널의 친구가 아니더라도 더 많은 카카오톡 이용자에게 브랜드와 상품을 알리고, 새로운 잠재고객과의 접점을 넓힐 수 있습니다.",
    emphasis: "카카오가 직접 운영하는 ‘카카오톡 브랜드픽’ 채널",
  },
  {
    number: "02",
    icon: Target,
    title: "맞춤형 메시지",
    tagline: "행동 데이터 기반으로 관심 있는 사용자만 선별",
    detail:
      "‘카카오톡 브랜드픽’ 채널의 친구 중 행동 데이터를 분석하고, 카카오의 추천 시스템을 기반으로 브랜드와 상품에 관심이 높은 사용자를 타겟팅하여 메시지를 전달합니다.",
  },
  {
    number: "03",
    icon: Gift,
    title: "혜택 중심의 메시지",
    tagline: "클릭하면 받는 혜택을 명확하게 전달",
    detail:
      "클릭 시 받을 혜택을 명확하게 전달해야 합니다.\n'할인 쿠폰', '즉시 적립 포인트', '무료 증정' 등 체감되는 혜택으로 클릭률과 전환을 높입니다.",
  },
  {
    number: "04",
    icon: Coins,
    title: "리워드 제공",
    tagline: "10초 체류 시 리워드 자동 지급",
    detail:
      "메시지 버튼으로 이동한 광고주 페이지에서 10초 이상 머무르면 유저에게 리워드가 지급됩니다.",
    note: "일부 랜딩의 경우 클릭 시 즉시 리워드가 지급될 수 있습니다. (PC에서 직접 클릭, 앱으로 이동, 서비스 브라우저 실행 등)",
  },
  {
    number: "05",
    icon: Newspaper,
    title: "서비스 피드 제공",
    tagline: "피드 노출로 타겟 밖 고객층까지 확장",
    detail:
      "발송된 메시지는 서비스 피드에도 함께 노출되어, 메시지 수신 대상 밖의 사용자에게도 브랜드를 알릴 수 있습니다.",
  },
];

const advantages = [
  {
    icon: MousePointerClick,
    title: "보유 고객 밖으로 확장",
    body: "광고주가 직접 보유한 고객 정보가 없어도 카카오 추천 기반으로 새로운 잠재고객에게 메시지를 보낼 수 있습니다.",
  },
  {
    icon: Sparkles,
    title: "관심 가능성 기반 추천",
    body: "브랜드와 캠페인 목적에 맞는 관심 가능성이 높은 고객군을 추천 받아 혜택형 메시지로 연결합니다.",
  },
  {
    icon: ShieldCheck,
    title: "처음 집행도 쉽게 시작",
    body: "비즈톡이 상담, 접수, 발송 운영, 결과 안내까지 함께 진행해 시작 부담을 낮춥니다.",
  },
];

const steps = [
  {
    label: "상담",
    body: "캠페인 목적, 업종, 예상 발송 건수, 희망 일정을 먼저 확인합니다.",
  },
  {
    label: "캠페인 접수",
    body: "혜택, 메시지 소재, 랜딩 경로 등 발송에 필요한 정보를 정리합니다.",
  },
  {
    label: "카카오 추천",
    body: "브랜드 목적에 맞는 관심 가능성이 높은 잠재고객군을 구성합니다.",
  },
  {
    label: "메시지 발송",
    body: "혜택형 카카오톡 메시지를 발송하고 초기 반응을 확인합니다.",
  },
  {
    label: "결과 안내",
    body: "발송 결과와 후속 집행 가능성을 상담으로 이어갑니다.",
  },
];

const faqs = [
  {
    q: "브랜드메시지와 무엇이 다른가요?",
    a: "브랜드메시지는 자사가 보유한 고객 정보와 마케팅 수신 동의를 기반으로 발송하는 마케팅 방식입니다. 카카오 브랜드픽은 브랜드픽 채널 친구, 즉 보유 고객 외 신규 잠재고객에게 혜택형 메시지를 발송하는 마케팅 방식입니다.",
  },
  {
    q: "최소 집행 조건이 있나요?",
    a: <>정가는 건당 100원이며, CBT 기간 중에는 건당 90원(VAT 별도)이 적용됩니다.<br />최소 10,000건부터 집행 가능해 최소 집행 금액은 900,000원(VAT 별도)입니다.</>,
  },
  {
    q: "소재와 운영은 어떻게 진행되나요?",
    a: <>상담을 통해 캠페인 목적과 혜택을 함께 정리한 뒤, 접수부터 발송 운영까지<br />비즈톡 신사업TF(tf@biztalk.co.kr)가 전담해 진행합니다.</>,
  },
];

export default function BrandPickPage() {
  const [scrollY, setScrollY] = useState(0);
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1440,
    h: typeof window !== "undefined" ? window.innerHeight : 900,
  }));
  const [openFaq, setOpenFaq] = useState(-1);
  const [openFeature, setOpenFeature] = useState(-1);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [consultForm, setConsultForm] = useState({
    companyName: "",
    contactName: "",
    phone: "",
    email: "",
    industry: "",
    expectedSendCount: "",
    content: "",
  });

  const heroRef = useRef<HTMLElement>(null);
  const conceptRef = useRef<HTMLElement>(null);
  const galleryRef = useRef<HTMLElement>(null);
  const processRef = useRef<HTMLElement>(null);
  const stepRefs = useRef<(HTMLElement | null)[]>([]);
  const compareBlockRef = useRef<HTMLDivElement>(null);
  const [compareBlockHeight, setCompareBlockHeight] = useState(0);

  useEffect(() => {
    const el = compareBlockRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      setCompareBlockHeight(entries[0].contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "[비즈톡] 카카오톡 브랜드픽";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    const fontLinkId = "brandpick-pretendard-font";
    if (document.getElementById(fontLinkId)) return undefined;

    const fontLink = document.createElement("link");
    fontLink.id = fontLinkId;
    fontLink.rel = "stylesheet";
    fontLink.href = "https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/variable/pretendardvariable.css";
    fontLink.crossOrigin = "anonymous";
    document.head.appendChild(fontLink);

    return () => fontLink.remove();
  }, []);

  useEffect(() => {
    // 기존 favicon 링크(sizes 지정된 것 포함)가 있으면 브라우저가 그쪽을 우선
    // 선택해버려서 새로 추가한 링크만으로는 파비콘이 안 바뀌는 경우가 있다.
    // 이 페이지에 머무는 동안은 기존 favicon 링크를 전부 비활성화하고
    // 브랜드픽 전용 favicon 하나만 남긴 뒤, 나갈 때 원래대로 복원한다.
    const existingIcons = Array.from(
      document.querySelectorAll<HTMLLinkElement>("link[rel~='icon']")
    );
    existingIcons.forEach((link) => link.parentNode?.removeChild(link));

    const favicon = document.createElement("link");
    favicon.rel = "icon";
    favicon.type = "image/x-icon";
    favicon.href = "/brandpick_favicon.ico";
    favicon.dataset.brandpickFavicon = "true";
    document.head.appendChild(favicon);

    apiUtil.asyncRequest({
      url: "/api/public/form/visit",
      method: apiUtil.requestMethod.POST,
      data: {
        formType: FORM_TYPE,
        visitorId: getVisitorId(),
        sourcePath: `${window.location.pathname}${window.location.search}`,
        referrer: document.referrer || "",
      },
    });

    return () => {
      favicon.remove();
      existingIcons.forEach((link) => document.head.appendChild(link));
    };
  }, []);

  useEffect(() => {
    const previousScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = previousScrollBehavior;
    };
  }, []);

  useEffect(() => {
    let raf = 0;
    let pending = false;

    const flush = () => {
      pending = false;
      setScrollY(window.scrollY);
    };
    const onScroll = () => {
      if (pending) return;
      pending = true;
      raf = requestAnimationFrame(flush);
    };
    const onResize = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(raf);
    };
  }, []);

  const sectionProgress = (element: HTMLElement | null) => {
    if (!element) return 0;
    const rect = element.getBoundingClientRect();
    const runway = Math.max(1, rect.height - viewport.h);
    return clamp01(-rect.top / runway);
  };

  const heroProgress = sectionProgress(heroRef.current);
  // sectionProgress alone only covers the sticky "pin" phase (however long
  // that runway is). Once it saturates at 1, the pinned block un-sticks and
  // scrolls normally for one full viewport height before the next section
  // appears — and since its content is already frozen at its end state by
  // then, that whole stretch reads as a dead static screen instead of a
  // scroll. Fading the pinned block out over that same stretch turns it into
  // an intentional dissolve instead.
  const heroReleaseProgress = (() => {
    const rect = heroRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    const runway = Math.max(1, rect.height - viewport.h);
    return clamp01((-rect.top - runway) / Math.max(1, viewport.h));
  })();
  const conceptProgress = sectionProgress(conceptRef.current);
  const galleryProgress = sectionProgress(galleryRef.current);
  const processProgress = sectionProgress(processRef.current);

  const heroTextOut = smoothstep(0.06, 0.36, heroProgress);
  const orbScale = lerp(1, viewport.w < 900 ? 2.1 : 2.65, smoothstep(0.15, 0.98, heroProgress));
  const orbX = lerp(viewport.w < 900 ? 0 : 120, 0, smoothstep(0.15, 0.98, heroProgress));
  const isCompactViewport = viewport.w < 768;
  // Mobile got compressed twice in a row chasing a "dead middle" bug (no
  // headline, no cards, just scattered particles) — fixing that by cutting
  // the intro/dissolve/reveal windows down to ~220px of scroll also made the
  // "브랜드픽이 필요한 순간" headline-to-cards handoff itself feel rushed,
  // which is a different complaint. h-[240vh] below gives this whole run
  // real room again: readable hold time on the headline, then dissolve and
  // reveal overlapping (no dead gap) at a pace closer to desktop's feel,
  // and the post-reveal hold before the next section stays generous too.
  const conceptIntro = smoothstep(isCompactViewport ? 0.02 : 0.06, isCompactViewport ? 0.14 : 0.2, conceptProgress);
  const burstProgress = smoothstep(isCompactViewport ? 0.22 : 0.32, isCompactViewport ? 0.42 : 0.6, conceptProgress);
  const compareReveal = smoothstep(isCompactViewport ? 0.32 : 0.48, isCompactViewport ? 0.5 : 0.72, conceptProgress);
  // Center the reveal block whenever there's room; below that, pin its top just
  // under the fixed header instead of letting the block go on being centered
  // and stray up behind it. Falls back to plain centering until the ResizeObserver
  // reports a real height on first paint.
  const compareTop =
    compareBlockHeight > 0
      ? Math.max(88, (viewport.h - compareBlockHeight) / 2)
      : viewport.h / 2;
  const activeStep = Math.min(steps.length - 1, Math.floor(processProgress * steps.length));
  const mobileActiveStepCount = steps.filter((_, index) => {
    const top = stepRefs.current[index]?.getBoundingClientRect().top;
    return top !== undefined && top < viewport.h * 0.45;
  }).length;
  const stepFillFraction =
    viewport.w < 768
      ? mobileActiveStepCount / steps.length
      : (activeStep + 1) / steps.length;

  const headerStyle = useMemo(
    () => ({
      backgroundColor:
        scrollY > viewport.h * 0.78 ? "rgba(255,255,255,0.86)" : "transparent",
      color: scrollY > viewport.h * 0.78 ? "#202020" : "#FFFFFF",
      borderColor:
        scrollY > viewport.h * 0.78 ? "rgba(17,17,17,0.08)" : "transparent",
      boxShadow:
        scrollY > viewport.h * 0.78
          ? "0 8px 24px -16px rgba(17,17,17,0.18)"
          : "none",
    }),
    [scrollY, viewport.h]
  );

  const moveToConsult = () => {
    document
      .getElementById("consult")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const moveToIntro = () => {
    document
      .getElementById("intro")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // These sections are tall scroll-jacked areas whose content fades in via
  // scroll progress. Jumping straight to their top (progress 0) lands on the
  // section's raw dark background before the entrance animation starts, which
  // looks like a blank screen. Scrolling a bit further in instead lands past
  // that fade-in.
  const moveIntoScrollSection = (id: string, entryFraction = 0.2) => {
    const section = document.getElementById(id);
    if (!section) return;
    const target =
      section.offsetTop + entryFraction * (section.offsetHeight - window.innerHeight);
    window.scrollTo({ top: target, behavior: "smooth" });
  };

  const updateConsultField = (
    field: keyof typeof consultForm,
    value: string
  ) => {
    const maxLength = CONSULT_FIELD_MAX_LENGTHS[field as keyof typeof CONSULT_FIELD_MAX_LENGTHS];
    const normalizedValue = maxLength === undefined ? value : value.slice(0, maxLength);
    setConsultForm((current) => ({ ...current, [field]: normalizedValue }));
  };

  const isConsultEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    consultForm.email.trim()
  );
  const isConsultFormReady =
    consultForm.companyName.trim() !== "" &&
    consultForm.contactName.trim() !== "" &&
    consultForm.phone.trim() !== "" &&
    isConsultEmailValid &&
    agreedToPrivacy;

  const submitConsult = () => {
    if (!isConsultFormReady || loading) return;
    apiUtil.asyncRequest({
      setLoading,
      url: "/api/public/form/submit",
      method: apiUtil.requestMethod.POST,
      data: {
        formType: FORM_TYPE,
        ...consultForm,
        privacyAgreed: agreedToPrivacy,
        sessionId: getVisitorId(),
        sourcePath: `${window.location.pathname}${window.location.search}`,
        referrer: document.referrer || "",
      },
      success: () => {
        alert("상담 신청이 접수되었습니다. 담당자가 확인 후 연락드리겠습니다.");
        setConsultForm({
          companyName: "",
          contactName: "",
          phone: "",
          email: "",
          industry: "",
          expectedSendCount: "",
          content: "",
        });
        setAgreedToPrivacy(false);
      },
    });
  };

  return (
    <div className="brandpick-page-root tw-relative tw-break-keep tw-bg-[#F8F7F2] tw-text-[#202020]">
      <IntroOverlay />
      <header
        className="tw-fixed tw-left-0 tw-right-0 tw-top-0 tw-z-50 tw-flex tw-items-center tw-justify-between tw-border-b tw-px-5 tw-py-4 tw-backdrop-blur-md tw-transition-all tw-duration-300 md:tw-px-10"
        style={headerStyle}
      >
        <a href="#top" className="tw-flex tw-items-center tw-gap-3">
          <img src="/biztalk-logo.svg" alt="비즈톡" className="tw-h-6 tw-w-auto" />
        </a>
        <nav className="tw-hidden tw-items-center tw-gap-6 tw-text-[12px] tw-font-bold md:tw-flex">
          <a
            href="#intro"
            onClick={(event) => {
              event.preventDefault();
              moveToIntro();
            }}
            className="tw-group tw-relative tw-py-1 tw-text-current/70 tw-transition-colors hover:tw-text-current"
          >
            상품소개
            <span className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw--bottom-0.5 tw-h-px tw-origin-left tw-scale-x-0 tw-bg-current tw-transition-transform tw-duration-300 tw-ease-out group-hover:tw-scale-x-100" />
          </a>
          <a
            href="#samples"
            onClick={(event) => {
              event.preventDefault();
              moveIntoScrollSection("samples");
            }}
            className="tw-group tw-relative tw-py-1 tw-text-current/70 tw-transition-colors hover:tw-text-current"
          >
            활용예시
            <span className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw--bottom-0.5 tw-h-px tw-origin-left tw-scale-x-0 tw-bg-current tw-transition-transform tw-duration-300 tw-ease-out group-hover:tw-scale-x-100" />
          </a>
          <a
            href="#process"
            className="tw-group tw-relative tw-py-1 tw-text-current/70 tw-transition-colors hover:tw-text-current"
          >
            진행방식
            <span className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw--bottom-0.5 tw-h-px tw-origin-left tw-scale-x-0 tw-bg-current tw-transition-transform tw-duration-300 tw-ease-out group-hover:tw-scale-x-100" />
          </a>
          <a
            href="#pricing"
            className="tw-group tw-relative tw-py-1 tw-text-current/70 tw-transition-colors hover:tw-text-current"
          >
            단가
            <span className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw--bottom-0.5 tw-h-px tw-origin-left tw-scale-x-0 tw-bg-current tw-transition-transform tw-duration-300 tw-ease-out group-hover:tw-scale-x-100" />
          </a>
          <a
            href="#faq"
            className="tw-group tw-relative tw-py-1 tw-text-current/70 tw-transition-colors hover:tw-text-current"
          >
            FAQ
            <span className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw--bottom-0.5 tw-h-px tw-origin-left tw-scale-x-0 tw-bg-current tw-transition-transform tw-duration-300 tw-ease-out group-hover:tw-scale-x-100" />
          </a>
        </nav>
        <button
          type="button"
          onClick={moveToConsult}
          className="tw-group tw-inline-flex tw-h-10 tw-items-center tw-gap-2 tw-rounded-md tw-bg-[#202020] tw-px-4 tw-text-[12px] tw-font-semibold tw-text-white tw-transition-all tw-duration-200 hover:tw-bg-[#343434] active:tw-scale-95"
        >
          상담신청
          <ArrowDownRight
            size={16}
            className="tw-transition-transform tw-duration-200 group-hover:tw-translate-x-0.5 group-hover:tw-translate-y-0.5"
          />
        </button>
      </header>

      <section
        id="top"
        ref={heroRef}
        className="tw-relative tw-h-[108vh] tw-overflow-hidden tw-bg-[#151515] tw-text-white md:tw-h-[160vh]"
      >
        <div
          className="tw-sticky tw-top-0 tw-h-screen tw-overflow-hidden"
          style={{
            opacity: 1 - heroReleaseProgress,
            willChange: "opacity",
          }}
        >
          <div
            className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-[54vh] md:tw-inset-auto md:tw-right-[-8%] md:tw-top-0 md:tw-h-full md:tw-w-[64vw]"
            style={{
              transform: `translateX(${orbX}px) scale(${orbScale})`,
              transformOrigin: viewport.w < 900 ? "center bottom" : "center",
              opacity: 1 - smoothstep(0.84, 1, heroProgress) * 0.35,
              willChange: "transform, opacity",
            }}
          >
            <FractalShader scrollProgress={heroProgress} />
          </div>
          <div className="tw-absolute tw-inset-0 tw-bg-[radial-gradient(circle_at_18%_18%,rgba(254,229,0,0.16),transparent_32%),linear-gradient(90deg,#151515_0%,rgba(21,21,21,0.92)_48%,rgba(21,21,21,0.32)_100%)]" />

          <main
            className="tw-relative tw-z-10 tw-flex tw-h-screen tw-max-w-[1440px] tw-flex-col tw-justify-center tw-px-6 tw-pt-20 md:tw-px-14 lg:tw-px-20"
            style={{
              opacity: 1 - heroTextOut,
              transform: `translateY(${-heroTextOut * 28}px)`,
              willChange: "opacity, transform",
            }}
          >
            <div className="tw-mb-7 tw-inline-flex tw-w-fit tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-white/14 tw-bg-white/8 tw-px-4 tw-py-2 tw-text-[12px] tw-font-semibold tw-text-[#FEE500]">
              <Send size={15} />
              비즈톡 뉴스레터로 먼저 만나는 카카오 신규 상품 소식
            </div>
            <h1
              className="tw-max-w-5xl tw-break-keep tw-font-semibold tw-leading-[1.05] tw-tracking-normal tw-text-white"
              style={{ fontSize: "clamp(42px, 6.7vw, 96px)" }}
            >
              우리 브랜드의 다음 고객,
              <br />
              카카오가 먼저 찾아줍니다
            </h1>
            <p className="tw-mt-8 tw-max-w-2xl tw-break-keep tw-text-base tw-leading-relaxed tw-text-white/72 md:tw-text-xl">
              보유 고객 DB 밖의 새로운 잠재고객에게도 카카오톡 마케팅 메시지를 발송해보세요.<br />
              상담부터 접수, 운영까지 비즈톡이 함께해 간편하게 시작할 수 있습니다.
            </p>
            <div className="tw-mt-10 tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row">
              <button
                type="button"
                onClick={moveToConsult}
                className="tw-group tw-inline-flex tw-h-14 tw-items-center tw-justify-center tw-gap-3 tw-rounded-md tw-bg-[#FEE500] tw-px-6 tw-text-sm tw-font-semibold tw-text-[#202020] tw-transition-all tw-duration-200 hover:tw-bg-white active:tw-scale-95"
              >
                상담 신청하기
                <ArrowDownRight
                  size={18}
                  className="tw-transition-transform tw-duration-200 group-hover:tw-translate-x-0.5 group-hover:tw-translate-y-0.5"
                />
              </button>
              <a
                href="#intro"
                onClick={(event) => {
                  event.preventDefault();
                  moveToIntro();
                }}
                className="tw-group tw-inline-flex tw-h-14 tw-items-center tw-justify-center tw-gap-3 tw-rounded-md tw-border tw-border-white/18 tw-px-6 tw-text-sm tw-font-semibold tw-text-white tw-transition-all tw-duration-200 hover:tw-bg-white/10 active:tw-scale-95"
              >
                자세히 보기
                <ChevronDown
                  size={18}
                  className="tw-transition-transform tw-duration-200 group-hover:tw-translate-y-0.5"
                />
              </a>
            </div>
          </main>
        </div>
        <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-24 tw-bg-gradient-to-b tw-from-transparent tw-to-white md:tw-h-40" />
      </section>

      <section id="intro" className="tw-break-keep tw-bg-white tw-px-6 tw-py-24 tw-text-[#202020] md:tw-px-14 md:tw-py-32">
        <div className="tw-mx-auto tw-max-w-[1120px]">
          <Reveal>
            <h2
              className="tw-break-keep tw-font-semibold tw-leading-tight tw-tracking-normal tw-text-[#202020]"
              style={{ fontSize: "clamp(28px, 3.4vw, 44px)" }}
            >
              카카오톡 브랜드픽이란
            </h2>
            <p className="tw-mt-5 tw-max-w-3xl tw-text-lg tw-leading-relaxed tw-text-[#3A3A34] md:tw-text-xl">
              {renderWithEmphasis(
                brandpickIntro,
                "카카오 소유의 '카카오톡 브랜드픽' 채널을 활용해"
              )}
            </p>
          </Reveal>

          <Reveal
            className="tw-mt-14 tw-rounded-xl tw-border tw-border-[#111111]/8 tw-bg-[#FAFAF7] tw-p-6 md:tw-mt-16 md:tw-p-8"
            delayMs={100}
          >
            <div className="tw-mb-5 tw-text-[13px] tw-font-semibold tw-text-[#8A7A00]">
              핵심 타겟팅 방식
            </div>
            <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-3">
              <div className="tw-group tw-cursor-default tw-rounded-lg tw-bg-white tw-p-5 tw-shadow-[0_1px_2px_rgba(17,17,17,0.04),0_10px_24px_-14px_rgba(17,17,17,0.25)] tw-transition-all tw-duration-300 tw-ease-out tw-will-change-transform hover:tw--translate-y-1.5 hover:tw-shadow-[0_1px_2px_rgba(17,17,17,0.06),0_22px_32px_-14px_rgba(17,17,17,0.32)]">
                <Users
                  size={22}
                  className="tw-text-[#8A7A00] tw-transition-transform tw-duration-300 group-hover:tw-scale-110"
                />
                <h3 className="tw-mt-4 tw-text-base tw-font-semibold tw-leading-tight">
                  연령·성별 기본 설정
                </h3>
                <p className="tw-mt-2 tw-text-sm tw-leading-relaxed tw-text-[#666]">
                  기본적인 연령대와 성별 조건을 선택해<br />
                  타겟 범위를 좁힐 수 있습니다.
                </p>
              </div>
              <div className="tw-group tw-cursor-default tw-rounded-lg tw-border tw-border-[#FEE500] tw-bg-[#FEE500] tw-p-5 tw-text-[#202020] tw-shadow-[0_1px_2px_rgba(109,97,0,0.12),0_10px_24px_-12px_rgba(109,97,0,0.4)] tw-transition-all tw-duration-300 tw-ease-out tw-will-change-transform hover:tw--translate-y-1.5 hover:tw-shadow-[0_1px_2px_rgba(109,97,0,0.14),0_22px_32px_-12px_rgba(109,97,0,0.5)]">
                <Brain
                  size={22}
                  className="tw-text-[#6D6100] tw-transition-transform tw-duration-300 group-hover:tw-scale-110"
                />
                <h3 className="tw-mt-4 tw-text-base tw-font-semibold tw-leading-tight">
                  관심사 기반 AI 매칭
                </h3>
                <p className="tw-mt-2 tw-text-sm tw-leading-relaxed tw-text-[#4E4600]">
                  업종·소재·유저 행태를 카카오 추천 시스템이<br />
                  종합 판단해, 브랜드에 딱 맞는 대상에게만 발송합니다.
                </p>
              </div>
              <div className="tw-group tw-cursor-default tw-rounded-lg tw-bg-white tw-p-5 tw-shadow-[0_1px_2px_rgba(17,17,17,0.04),0_10px_24px_-14px_rgba(17,17,17,0.25)] tw-transition-all tw-duration-300 tw-ease-out tw-will-change-transform hover:tw--translate-y-1.5 hover:tw-shadow-[0_1px_2px_rgba(17,17,17,0.06),0_22px_32px_-14px_rgba(17,17,17,0.32)]">
                <UserMinus
                  size={22}
                  className="tw-text-[#8A7A00] tw-transition-transform tw-duration-300 group-hover:tw-scale-110"
                />
                <h3 className="tw-mt-4 tw-text-base tw-font-semibold tw-leading-tight">
                  보유 채널 친구 제외
                </h3>
                <p className="tw-mt-2 tw-text-sm tw-leading-relaxed tw-text-[#666]">
                  카카오 채널을 운영 중이라면 기존 채널 친구는 제외하고,
                  온전히 신규 고객에게만 발송할 수 있습니다.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal>
          <div className="tw-mb-2 tw-mt-16 tw-text-xl tw-font-semibold tw-text-[#202020] md:tw-text-2xl">
            카카오 브랜드픽 특징
          </div>
          <div className="tw-divide-y tw-divide-[#111111]/8 tw-border-t tw-border-[#111111]/8">
            {brandpickFeatures.map((feature, index) => {
              const Icon = feature.icon;
              const isOpen = openFeature === index;
              return (
                <div
                  key={feature.number}
                  className={`tw-border-l-[3px] tw-transition-colors ${
                    isOpen ? "tw-border-[#FEE500] tw-bg-[#FFFBEA]" : "tw-border-transparent"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFeature(isOpen ? -1 : index)}
                    className={`tw-flex tw-w-full tw-items-center tw-gap-5 tw-py-6 tw-pl-5 tw-pr-6 tw-text-left tw-outline-none tw-transition-colors focus-visible:tw-bg-[#FAFAF7] md:tw-gap-7 md:tw-pl-7 ${
                      isOpen ? "" : "hover:tw-bg-[#FAFAF7]"
                    }`}
                  >
                    <span
                      className={`tw-grid tw-h-11 tw-w-11 tw-shrink-0 tw-place-items-center tw-rounded-md tw-transition-colors md:tw-h-12 md:tw-w-12 ${
                        isOpen ? "tw-bg-[#FEE500]" : "tw-bg-[#F2F1EA]"
                      }`}
                    >
                      <Icon size={20} className="tw-text-[#202020]" />
                    </span>
                    <div className="tw-min-w-0 tw-flex-1">
                      <div className="tw-flex tw-items-center tw-gap-2 tw-text-[12px] tw-font-semibold tw-text-[#8A7A00]">
                        {feature.number}
                      </div>
                      <div className="tw-mt-1 tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-3 tw-gap-y-1">
                        <h3 className="tw-text-lg tw-font-semibold tw-leading-tight md:tw-text-xl">
                          {feature.title}
                        </h3>
                        <span className="tw-text-sm tw-text-[#767668]">
                          {feature.tagline}
                        </span>
                      </div>
                    </div>
                    <ChevronDown
                      size={20}
                      className={`tw-shrink-0 tw-text-[#8A8A82] tw-transition-transform ${
                        isOpen ? "tw-rotate-180" : ""
                      }`}
                    />
                  </button>
                  <div
                    className="tw-grid tw-transition-[grid-template-rows] tw-duration-300 tw-ease-out"
                    style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
                  >
                    <div className="tw-overflow-hidden">
                      <p className="tw-max-w-2xl tw-pb-6 tw-pl-20 tw-pr-6 tw-text-sm tw-leading-relaxed tw-text-[#555] md:tw-pl-[104px] md:tw-text-base">
                        {renderWithEmphasis(feature.detail, feature.emphasis)}
                      </p>
                      {feature.note && (
                        <p className="tw-max-w-2xl tw-pb-6 tw-pl-20 tw-pr-6 tw-text-xs tw-leading-relaxed tw-text-[#999] md:tw-pl-[104px]">
                          * {feature.note}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          </Reveal>
        </div>
      </section>

      <section
        id="about"
        ref={conceptRef}
        className="tw-relative tw-h-[240vh] tw-bg-[#151515] tw-text-white"
      >
        <div className="tw-sticky tw-top-0 tw-flex tw-h-screen tw-items-center tw-justify-center tw-overflow-hidden tw-px-6 md:tw-px-14">
          <div
            className="tw-absolute tw-inset-0"
            style={{
              opacity: 1 - compareReveal * 0.75,
              background:
                "radial-gradient(circle at 50% 50%, rgba(254,229,0,0.2), transparent 34%), #151515",
            }}
          />

          <ParticleBurstText
            progress={burstProgress}
            className="tw-relative tw-z-10 tw-w-full tw-max-w-[1120px] tw-text-center"
          >
            <div
              style={{
                opacity: conceptIntro * (1 - smoothstep(0.42, 0.7, conceptProgress)),
                transform: `translateY(${lerp(46, 0, conceptIntro)}px)`,
                willChange: "opacity, transform",
              }}
            >
              <div className="tw-mb-8 tw-flex tw-items-center tw-justify-center tw-gap-3 tw-text-[12px] tw-font-semibold tw-text-[#FEE500]">
                <span className="tw-h-px tw-w-8 tw-bg-[#FEE500]" />
                브랜드픽이 필요한 순간
                <span className="tw-h-px tw-w-8 tw-bg-[#FEE500]" />
              </div>
              <h2
                className="tw-break-keep tw-font-semibold tw-leading-[1.04] tw-tracking-normal tw-text-white"
                style={{ fontSize: "clamp(42px, 7vw, 104px)" }}
              >
                브랜드의 다음 고객을
                <br />
                카카오가 찾아줍니다
              </h2>
              <p className="tw-mx-auto tw-mt-8 tw-max-w-2xl tw-text-base tw-leading-relaxed tw-text-white/68 md:tw-text-xl">
                브랜드픽은 광고주 DB 안에서만 찾던 고객을<br />
                카카오 추천 기반의 신규 잠재고객으로 확장하는 메시지 상품입니다.
              </p>
            </div>
          </ParticleBurstText>

          <div
            ref={compareBlockRef}
            className="tw-absolute tw-inset-x-0 tw-z-20 tw-mx-auto tw-flex tw-max-w-[1180px] tw-flex-col tw-gap-7 tw-px-6 md:tw-gap-12 md:tw-px-14"
            style={{
              top: compareTop,
              transform: `translateY(${(1 - compareReveal) * 36}px)`,
              pointerEvents: compareReveal > 0.4 ? "auto" : "none",
              willChange: "top, transform",
            }}
          >
            <div className="tw-flex tw-justify-center">
              <BoundaryDiagram progress={compareReveal} />
            </div>
            <div
              className="tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-2 md:tw-gap-5"
              style={{ opacity: compareReveal, willChange: "opacity" }}
            >
              <div className="tw-group tw-cursor-default tw-rounded-lg tw-border tw-border-white/12 tw-bg-white/8 tw-p-3.5 tw-shadow-[0_1px_1px_rgba(255,255,255,0.06),0_24px_44px_-24px_rgba(0,0,0,0.7)] tw-backdrop-blur-md tw-transition-all tw-duration-300 tw-ease-out tw-will-change-transform hover:tw--translate-y-2 hover:tw-border-white/22 hover:tw-bg-white/12 hover:tw-shadow-[0_1px_1px_rgba(255,255,255,0.08),0_32px_52px_-22px_rgba(0,0,0,0.8)] md:tw-p-7">
                <div className="tw-text-[13px] tw-font-semibold tw-text-white/52">
                  브랜드메시지
                </div>
                <h3 className="tw-mt-2 tw-text-2xl tw-font-semibold tw-leading-tight tw-text-white md:tw-mt-5 md:tw-text-3xl">
                  보유 고객 데이터와
                  <br />
                  마케팅 수신 동의 기반
                </h3>
                <p className="tw-mt-2 tw-text-sm tw-leading-relaxed tw-text-white/62 md:tw-mt-5">
                  이미 확보한 고객 정보 안에서 타겟을 구성하고 메시지를 발송합니다.
                </p>
              </div>
              <div className="tw-group tw-cursor-default tw-rounded-lg tw-border tw-border-[#FEE500] tw-bg-[#FEE500] tw-p-3.5 tw-text-[#202020] tw-shadow-[0_1px_1px_rgba(109,97,0,0.15),0_0_50px_-14px_rgba(254,229,0,0.45),0_28px_48px_-22px_rgba(109,97,0,0.6)] tw-transition-all tw-duration-300 tw-ease-out tw-will-change-transform hover:tw--translate-y-2 hover:tw-shadow-[0_1px_1px_rgba(109,97,0,0.18),0_0_64px_-12px_rgba(254,229,0,0.6),0_36px_56px_-20px_rgba(109,97,0,0.7)] md:tw-p-7">
                <div className="tw-text-[13px] tw-font-semibold tw-text-[#6D6100]">
                  브랜드픽
                </div>
                <h3 className="tw-mt-2 tw-text-2xl tw-font-semibold tw-leading-tight tw-text-[#202020] md:tw-mt-5 md:tw-text-3xl">
                  카카오 추천 기반<br />잠재고객 메시지
                </h3>
                <p className="tw-mt-2 tw-text-sm tw-leading-relaxed tw-text-[#4E4600] md:tw-mt-5">
                  보유 고객 DB 밖의 신규 고객에게 혜택형 메시지를 전달합니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="tw-px-6 tw-py-24 md:tw-px-14 md:tw-py-32">
        <div className="tw-mx-auto tw-max-w-[1240px]">
          <Reveal>
            <div className="tw-mb-4 tw-text-[13px] tw-font-semibold tw-text-[#8A7A00]">
              핵심 장점
            </div>
            <h2
              className="tw-max-w-4xl tw-break-keep tw-font-semibold tw-leading-[1.08] tw-tracking-normal tw-text-[#202020]"
              style={{ fontSize: "clamp(32px, 4.4vw, 64px)" }}
            >
              설명은 충분히,
              <br />
              판단은 빠르게 할 수 있도록
            </h2>
          </Reveal>
          <div className="tw-mt-12 tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-3">
            {advantages.map((item, index) => {
              const Icon = item.icon;
              return (
                <Reveal key={item.title} delayMs={index * 100}>
                <article
                  className="tw-group tw-cursor-default tw-rounded-lg tw-bg-white tw-p-6 tw-shadow-[0_1px_2px_rgba(17,17,17,0.04),0_10px_24px_-14px_rgba(17,17,17,0.25)] tw-transition-all tw-duration-300 tw-ease-out tw-will-change-transform hover:tw--translate-y-1.5 hover:tw-shadow-[0_1px_2px_rgba(17,17,17,0.06),0_22px_32px_-14px_rgba(17,17,17,0.32)]"
                >
                  <Icon
                    size={24}
                    className="tw-text-[#8A7A00] tw-transition-transform tw-duration-300 group-hover:tw-scale-110"
                  />
                  <h3 className="tw-mt-5 tw-text-xl tw-font-semibold tw-leading-tight">{item.title}</h3>
                  <p className="tw-mt-3 tw-text-sm tw-leading-relaxed tw-text-[#666]">{item.body}</p>
                </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section id="samples" ref={galleryRef} className="tw-relative tw-h-[300vh] tw-text-white">
        <div className="tw-sticky tw-top-0 tw-h-screen tw-overflow-hidden">
          <HorizontalGallery
            progress={smoothstep(0.08, 0.95, galleryProgress)}
            entrance={smoothstep(0, 0.16, galleryProgress)}
          />
        </div>
      </section>

      <section
        id="process"
        ref={processRef}
        className="tw-relative tw-bg-white tw-text-[#202020] md:tw-h-[260vh]"
      >
        <div className="tw-relative tw-flex tw-min-h-screen tw-items-center tw-overflow-visible tw-px-6 tw-py-24 md:tw-sticky md:tw-top-0 md:tw-h-screen md:tw-overflow-hidden md:tw-px-14 md:tw-py-0">
          <div className="tw-mx-auto tw-grid tw-w-full tw-max-w-[1240px] tw-grid-cols-1 tw-gap-10 lg:tw-grid-cols-12 lg:tw-items-center">
            <div className="lg:tw-col-span-5">
              <Reveal>
                <div className="tw-mb-5 tw-text-[13px] tw-font-semibold tw-text-[#8A7A00]">
                  진행 방식
                </div>
                <h2
                  className="tw-break-keep tw-font-semibold tw-leading-[1.08] tw-tracking-normal tw-text-[#202020]"
                  style={{ fontSize: "clamp(32px, 4.4vw, 64px)" }}
                >
                  상담부터 발송,
                  <br />
                  결과 안내까지
                </h2>
                <p className="tw-mt-6 tw-max-w-md tw-text-base tw-leading-relaxed tw-text-[#666]">
                  복잡한 절차 없이 상담부터 결과 안내까지 5단계로 진행됩니다.<br />
                  각 단계마다 담당자가 함께합니다.
                </p>
              </Reveal>
            </div>
            <div className="tw-relative lg:tw-col-span-7">
              <div className="tw-absolute tw-left-5 tw-top-6 tw-bottom-6 tw-w-px tw-bg-[#202020]/12 md:tw-left-7" />
              <div
                className="tw-absolute tw-left-5 tw-top-6 tw-bottom-6 tw-w-px tw-bg-[#FEE500] tw-transition-transform tw-duration-500 tw-ease-out md:tw-left-7"
                style={{
                  transform: `scaleY(${clamp01(stepFillFraction)})`,
                  transformOrigin: "top",
                }}
              />
              <div className="tw-grid tw-gap-3">
                {steps.map((step, index) => {
                  // On mobile the section isn't pinned/sticky (see the md:sticky
                  // class above), so there's no single shared "processProgress"
                  // driving a step-by-step reveal the way desktop has. This used
                  // to just mark every step active unconditionally on mobile,
                  // which rendered them all solid black from the start instead
                  // of activating one at a time while scrolling. Each step now
                  // activates on its own once it's scrolled far enough up the
                  // viewport, mirroring the desktop effect without needing the
                  // sticky container.
                  const mobileStepTop = stepRefs.current[index]?.getBoundingClientRect().top;
                  const mobileActive = mobileStepTop !== undefined && mobileStepTop < viewport.h * 0.45;
                  const active = viewport.w < 768 ? mobileActive : index <= activeStep;
                  return (
                    <article
                      key={step.label}
                      ref={(el) => {
                        stepRefs.current[index] = el;
                      }}
                      className={`tw-relative tw-rounded-lg tw-border tw-p-5 tw-transition-all tw-duration-500 md:tw-p-6 ${
                        active
                          ? "tw-border-[#202020] tw-bg-[#202020] tw-text-white"
                          : "tw-border-[#202020]/10 tw-bg-[#F8F7F2] tw-text-[#202020]"
                      }`}
                      style={{
                        transform: `translateX(${active ? 0 : 18}px)`,
                        opacity: active ? 1 : 0.58,
                      }}
                    >
                      <div className="tw-flex tw-items-start tw-gap-4">
                        <span
                          className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-md tw-text-sm tw-font-semibold ${
                            active ? "tw-bg-[#FEE500] tw-text-[#202020]" : "tw-bg-white tw-text-[#8A7A00]"
                          }`}
                        >
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <div>
                          <h3 className={`tw-text-xl tw-font-semibold ${active ? "tw-text-white" : "tw-text-[#202020]"}`}>{step.label}</h3>
                          <p className={`tw-mt-2 tw-text-sm tw-leading-relaxed ${active ? "tw-text-white/68" : "tw-text-[#666]"}`}>
                            {step.body}
                          </p>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="tw-bg-[#FEE500] tw-px-6 tw-py-20 md:tw-px-14">
        <div className="tw-mx-auto tw-max-w-[1240px]">
          <Reveal>
            <div className="tw-text-[13px] tw-font-semibold tw-text-[#685B00]">
              단가
            </div>
            <h2 className="tw-mt-4 tw-max-w-2xl tw-break-keep tw-text-4xl tw-font-semibold tw-tracking-normal tw-text-[#202020] md:tw-text-6xl">
              CBT 기간, 합리적인 단가로 시작하세요
            </h2>
          </Reveal>

          <div className="tw-mt-10 tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-3">
            <Reveal delayMs={0}>
            <div className="tw-group tw-cursor-default tw-rounded-lg tw-bg-white/55 tw-p-6 tw-shadow-[0_1px_2px_rgba(109,97,0,0.08),0_10px_22px_-14px_rgba(109,97,0,0.45)] tw-transition-all tw-duration-300 tw-ease-out tw-will-change-transform hover:tw--translate-y-1.5 hover:tw-bg-white/70 hover:tw-shadow-[0_1px_2px_rgba(109,97,0,0.1),0_22px_30px_-14px_rgba(109,97,0,0.55)]">
              <div className="tw-text-sm tw-font-bold tw-text-[#685B00]">건당 단가</div>
              <div className="tw-mt-2 tw-flex tw-items-baseline tw-gap-2">
                <span className="tw-text-lg tw-font-semibold tw-text-[#8A7A00] tw-line-through">
                  100원
                </span>
                <span className="tw-text-4xl tw-font-bold tw-text-[#202020]">90원</span>
              </div>
              <div className="tw-mt-1 tw-text-xs tw-font-bold tw-text-[#685B00]">
                VAT 별도 · CBT 기간 한정가
              </div>
            </div>
            </Reveal>
            <Reveal delayMs={100}>
            <div className="tw-group tw-cursor-default tw-rounded-lg tw-bg-white/55 tw-p-6 tw-shadow-[0_1px_2px_rgba(109,97,0,0.08),0_10px_22px_-14px_rgba(109,97,0,0.45)] tw-transition-all tw-duration-300 tw-ease-out tw-will-change-transform hover:tw--translate-y-1.5 hover:tw-bg-white/70 hover:tw-shadow-[0_1px_2px_rgba(109,97,0,0.1),0_22px_30px_-14px_rgba(109,97,0,0.55)]">
              <div className="tw-text-sm tw-font-bold tw-text-[#685B00]">
                최소 발송 건수
              </div>
              <div className="tw-mt-2 tw-text-4xl tw-font-bold tw-text-[#202020]">
                10,000건
              </div>
              <div className="tw-mt-1 tw-text-xs tw-font-bold tw-text-[#685B00]">
                최소 집행 기준
              </div>
            </div>
            </Reveal>
            <Reveal delayMs={200}>
            <div className="tw-group tw-cursor-default tw-rounded-lg tw-bg-white/55 tw-p-6 tw-shadow-[0_1px_2px_rgba(109,97,0,0.08),0_10px_22px_-14px_rgba(109,97,0,0.45)] tw-transition-all tw-duration-300 tw-ease-out tw-will-change-transform hover:tw--translate-y-1.5 hover:tw-bg-white/70 hover:tw-shadow-[0_1px_2px_rgba(109,97,0,0.1),0_22px_30px_-14px_rgba(109,97,0,0.55)]">
              <div className="tw-text-sm tw-font-bold tw-text-[#685B00]">
                최소 집행 금액
              </div>
              <div className="tw-mt-2 tw-flex tw-flex-wrap tw-items-baseline tw-gap-2">
                <span className="tw-text-base tw-font-semibold tw-text-[#8A7A00] tw-line-through">
                  1,000,000원
                </span>
                <span className="tw-text-4xl tw-font-bold tw-text-[#202020]">
                  900,000원
                </span>
              </div>
              <div className="tw-mt-1 tw-text-xs tw-font-bold tw-text-[#685B00]">
                90원 × 10,000건, VAT 별도
              </div>
            </div>
            </Reveal>
          </div>

          <button
            type="button"
            onClick={moveToConsult}
            className="tw-group tw-mt-10 tw-inline-flex tw-h-14 tw-items-center tw-justify-center tw-gap-3 tw-rounded-md tw-bg-[#202020] tw-px-6 tw-text-sm tw-font-semibold tw-text-white tw-transition-all tw-duration-200 hover:tw-bg-[#343434] active:tw-scale-95"
          >
            상담 신청으로 이동
            <ArrowDownRight
              size={18}
              className="tw-transition-transform tw-duration-200 group-hover:tw-translate-x-0.5 group-hover:tw-translate-y-0.5"
            />
          </button>
        </div>
      </section>

      <section id="faq" className="tw-px-6 tw-py-24 md:tw-px-14 md:tw-py-32">
        <div className="tw-mx-auto tw-grid tw-max-w-[1120px] tw-grid-cols-1 tw-gap-10 lg:tw-grid-cols-12">
          <Reveal className="lg:tw-col-span-4">
            <div className="tw-mb-4 tw-text-[13px] tw-font-semibold tw-text-[#8A7A00]">
              FAQ
            </div>
            <h2 className="tw-text-4xl tw-font-semibold tw-leading-tight tw-tracking-normal tw-text-[#202020] md:tw-text-5xl">
              상담 전<br />많이 묻는 질문
            </h2>
          </Reveal>
          <Reveal className="lg:tw-col-span-8" delayMs={100}>
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <button
                  key={faq.q}
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? -1 : index)}
                  className="tw-block tw-w-full tw-border-t tw-border-[#111111]/12 tw-px-2 tw-py-6 tw-text-left tw-outline-none tw-transition-colors last:tw-border-b hover:tw-bg-[#FAFAF7] focus-visible:tw-bg-[#FAFAF7]"
                >
                  <div className="tw-flex tw-items-center tw-justify-between tw-gap-6">
                    <span className="tw-text-xl tw-font-semibold">{faq.q}</span>
                    <ChevronDown
                      size={22}
                      className={`tw-shrink-0 tw-transition-transform tw-duration-300 ${
                        isOpen ? "tw-rotate-180" : ""
                      }`}
                    />
                  </div>
                  <div
                    className="tw-grid tw-transition-[grid-template-rows] tw-duration-300 tw-ease-out"
                    style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
                  >
                    <div className="tw-overflow-hidden">
                      <p className="tw-mt-4 tw-max-w-2xl tw-text-sm tw-leading-relaxed tw-text-[#666] md:tw-text-base">
                        {faq.a}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </Reveal>
        </div>
      </section>

      <section id="consult" className="tw-bg-[#202020] tw-px-6 tw-py-24 tw-text-white md:tw-px-14 md:tw-py-32">
        <div className="tw-mx-auto tw-grid tw-max-w-[1240px] tw-grid-cols-1 tw-gap-12 lg:tw-grid-cols-12">
          <Reveal className="lg:tw-col-span-5">
            <div className="tw-mb-4 tw-text-[13px] tw-font-semibold tw-text-[#FEE500]">
              상담 신청
            </div>
            <h2
              className="tw-break-keep tw-font-semibold tw-leading-[1.08] tw-tracking-normal tw-text-white"
              style={{ fontSize: "clamp(32px, 4.4vw, 64px)" }}
            >
              브랜드픽 상담을 남겨주세요
            </h2>
            <p className="tw-mt-6 tw-max-w-md tw-whitespace-pre-line tw-text-base tw-leading-relaxed tw-text-white/68">
              {"입력하신 정보는 카카오 브랜드픽 상담을 위한 목적으로만 사용되며,\n그 외의 목적으로는 이용되지 않습니다.\n담당자가 신청 내용을 확인한 후 순차적으로 안내드릴 예정이오니,\n입력하신 E-mail 및 휴대폰 번호를 확인해 주세요."}
            </p>
          </Reveal>
          <Reveal
            className="tw-grid tw-gap-4 tw-rounded-lg tw-bg-white tw-p-5 tw-text-[#202020] md:tw-p-7 lg:tw-col-span-7"
            delayMs={100}
          >
          <form className="tw-contents">
            <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
              <label className="tw-grid tw-gap-2 tw-text-sm tw-font-bold">
                <span>
                  회사명 <span className="tw-text-red-500">*</span>
                </span>
                <input
                  required
                  maxLength={CONSULT_FIELD_MAX_LENGTHS.companyName}
                  value={consultForm.companyName}
                  onChange={(event) =>
                    updateConsultField("companyName", event.target.value)
                  }
                  className="tw-h-12 tw-rounded-md tw-border tw-border-[#202020]/12 tw-px-4 tw-outline-none tw-transition-all tw-duration-200 focus:tw-border-[#202020] focus:tw-ring-4 focus:tw-ring-[#202020]/8"
                  placeholder="회사명을 입력하세요"
                />
              </label>
              <label className="tw-grid tw-gap-2 tw-text-sm tw-font-bold">
                <span>
                  담당자명 <span className="tw-text-red-500">*</span>
                </span>
                <input
                  required
                  maxLength={CONSULT_FIELD_MAX_LENGTHS.contactName}
                  value={consultForm.contactName}
                  onChange={(event) =>
                    updateConsultField("contactName", event.target.value)
                  }
                  className="tw-h-12 tw-rounded-md tw-border tw-border-[#202020]/12 tw-px-4 tw-outline-none tw-transition-all tw-duration-200 focus:tw-border-[#202020] focus:tw-ring-4 focus:tw-ring-[#202020]/8"
                  placeholder="담당자명을 입력하세요"
                />
              </label>
              <label className="tw-grid tw-gap-2 tw-text-sm tw-font-bold">
                <span>
                  연락처 <span className="tw-text-red-500">*</span>
                </span>
                <input
                  required
                  value={consultForm.phone}
                  onChange={(event) =>
                    updateConsultField(
                      "phone",
                      formatPhoneNumber(event.target.value)
                    )
                  }
                  className="tw-h-12 tw-rounded-md tw-border tw-border-[#202020]/12 tw-px-4 tw-outline-none tw-transition-all tw-duration-200 focus:tw-border-[#202020] focus:tw-ring-4 focus:tw-ring-[#202020]/8"
                  placeholder="010-0000-0000"
                />
              </label>
              <label className="tw-grid tw-gap-2 tw-text-sm tw-font-bold">
                <span>
                  이메일 <span className="tw-text-red-500">*</span>
                </span>
                <input
                  required
                  type="email"
                  maxLength={CONSULT_FIELD_MAX_LENGTHS.email}
                  value={consultForm.email}
                  onChange={(event) =>
                    updateConsultField("email", event.target.value)
                  }
                  className="tw-h-12 tw-rounded-md tw-border tw-border-[#202020]/12 tw-px-4 tw-outline-none tw-transition-all tw-duration-200 focus:tw-border-[#202020] focus:tw-ring-4 focus:tw-ring-[#202020]/8"
                  placeholder="name@company.com"
                />
              </label>
              <label className="tw-grid tw-gap-2 tw-text-sm tw-font-bold">
                업종
                <input
                  value={consultForm.industry}
                  onChange={(event) => updateConsultField("industry", event.target.value)}
                  className="tw-h-12 tw-rounded-md tw-border tw-border-[#202020]/12 tw-px-4 tw-outline-none tw-transition-all tw-duration-200 focus:tw-border-[#202020] focus:tw-ring-4 focus:tw-ring-[#202020]/8"
                  placeholder="예: 뷰티, F&B, 교육"
                />
              </label>
              <label className="tw-grid tw-gap-2 tw-text-sm tw-font-bold">
                예상 발송 건수
                <input
                  value={consultForm.expectedSendCount}
                  onChange={(event) => updateConsultField("expectedSendCount", event.target.value)}
                  className="tw-h-12 tw-rounded-md tw-border tw-border-[#202020]/12 tw-px-4 tw-outline-none tw-transition-all tw-duration-200 focus:tw-border-[#202020] focus:tw-ring-4 focus:tw-ring-[#202020]/8"
                  placeholder="예: 10,000건"
                />
              </label>
            </div>
            <label className="tw-grid tw-gap-2 tw-text-sm tw-font-bold">
              문의 내용
              <textarea
                value={consultForm.content}
                onChange={(event) =>
                  updateConsultField(
                    "content",
                    event.target.value.slice(0, CONSULT_MESSAGE_MAX_LENGTH)
                  )
                }
                maxLength={CONSULT_MESSAGE_MAX_LENGTH}
                className="tw-min-h-28 tw-rounded-md tw-border tw-border-[#202020]/12 tw-px-4 tw-py-3 tw-outline-none tw-transition-all tw-duration-200 focus:tw-border-[#202020] focus:tw-ring-4 focus:tw-ring-[#202020]/8"
                placeholder="희망 일정, 타겟, 예산, 캠페인 목적을 자유롭게 남겨주세요"
              />
              <span className="tw-text-right tw-text-xs tw-font-medium tw-text-[#999]">
                {consultForm.content.length}/{CONSULT_MESSAGE_MAX_LENGTH}자
              </span>
            </label>
            <div className="tw-grid tw-gap-3">
              <div className="tw-rounded-md tw-border tw-border-[#111111]/10 tw-bg-[#F8F7F2] tw-p-4 tw-text-xs tw-leading-relaxed tw-text-[#555]">
                <p className="tw-mb-2 tw-font-semibold tw-text-[#333]">
                  비즈톡에서 제공하는 서비스를 원활히 제공받기 위하여,
                  아래와 같이 개인정보의 수집 및 이용에 동의합니다.
                </p>
                <ol className="tw-list-decimal tw-space-y-1 tw-pl-4">
                  <li>
                    <span className="tw-font-semibold tw-text-[#333]">수집 항목:</span>{" "}
                    회사명, 담당자명, 연락처(휴대폰 번호), 이메일, 문의 내용
                  </li>
                  <li>
                    <span className="tw-font-semibold tw-text-[#333]">수집 및 이용 목적:</span>{" "}
                    비즈톡 상담 신청 접수 및 처리
                  </li>
                  <li>
                    <span className="tw-font-semibold tw-text-[#333]">보유 및 이용 기간:</span>{" "}
                    접수 완료일로부터 90일간 보관 후 파기
                  </li>
                  <li>
                    <span className="tw-font-semibold tw-text-[#333]">동의 거부 권리 및 불이익:</span>{" "}
                    귀하는 개인정보 수집·이용에 대한 동의를 거부하실 권리가
                    있으며, 다만 동의하지 않으실 경우 온라인 상담 신청이
                    제한될 수 있습니다.
                  </li>
                </ol>
              </div>
              <label className="tw-flex tw-items-start tw-gap-3 tw-text-sm tw-font-bold tw-text-[#444]">
                <input
                  type="checkbox"
                  checked={agreedToPrivacy}
                  onChange={(event) => setAgreedToPrivacy(event.target.checked)}
                  className="tw-mt-1 tw-h-4 tw-w-4 tw-accent-[#202020] tw-transition-transform tw-duration-150 checked:tw-scale-110"
                />
                <span>
                  개인정보 수집 및 이용에 동의합니다.{" "}
                  <span className="tw-text-red-500">*</span>
                </span>
              </label>
              <p className="tw-m-0 tw-rounded-md tw-border tw-border-[#FEE500] tw-bg-[#FFFBE6] tw-px-4 tw-py-3 tw-text-xs tw-leading-relaxed tw-text-[#5C5300]">
                ※ 카카오 심사 기준에 따라 일부 업종은 브랜드픽 캠페인 집행이 제한되거나 불가할 수 있습니다.
              </p>
            </div>
            <button
              type="button"
              disabled={!isConsultFormReady || loading}
              onClick={submitConsult}
              className={`tw-group tw-mt-2 tw-inline-flex tw-h-14 tw-items-center tw-justify-center tw-gap-3 tw-rounded-md tw-px-6 tw-text-sm tw-font-semibold tw-transition-all tw-duration-200 ${
                isConsultFormReady && !loading
                  ? "tw-bg-[#202020] tw-text-white hover:tw-bg-[#343434] active:tw-scale-95"
                  : "tw-cursor-not-allowed tw-bg-[#D8D8D2] tw-text-[#8A8A82]"
              }`}
            >
              {loading ? "접수 중입니다" : "상담 신청하기"}
              <MessageSquareText
                size={18}
                className="tw-transition-transform tw-duration-200 group-hover:tw-translate-x-0.5"
              />
            </button>
          </form>
          </Reveal>
        </div>
      </section>

      <footer className="tw-border-t tw-border-[#111111]/8 tw-bg-[#F8F7F2] tw-px-6 tw-py-12 tw-text-[#3A3A34] md:tw-px-14">
        <div className="tw-mx-auto tw-flex tw-max-w-[1240px] tw-flex-col tw-items-center tw-justify-between tw-gap-6 md:tw-flex-row">
          <div className="tw-flex tw-items-center">
            <img src="/biztalk-logo.svg" alt="비즈톡" className="tw-h-6 tw-w-auto" />
          </div>
          <a
            href="mailto:tf@biztalk.co.kr"
            className="tw-group tw-inline-flex tw-items-center tw-gap-2.5 tw-rounded-full tw-border tw-border-[#202020]/12 tw-bg-white tw-px-5 tw-py-3 tw-text-sm tw-font-semibold tw-text-[#202020] tw-shadow-sm tw-transition-all tw-duration-200 hover:tw--translate-y-0.5 hover:tw-border-[#202020]/24 hover:tw-bg-[#FFFBEA] hover:tw-shadow-md active:tw-scale-95"
          >
            <Mail size={16} className="tw-text-[#8A7A00] tw-transition-transform tw-duration-200 group-hover:tw-scale-110" />
            문의 tf@biztalk.co.kr
          </a>
        </div>
      </footer>
    </div>
  );
}
